import { ProductSource } from '../product-types';
import { resetStepData } from '../reducers/product-flow.reducer';
import { ProductWithId, getProductQuantity, isFormProduct } from '../utils/product-utils';
import { getStockStatus } from '../utils/stock-product';
import { ProductPreflightService } from './product-preflight.service';
import { Injectable } from '@angular/core';
import { uploadEditorFile } from '@app/api/CustomersCanvas';
import { GET_PRICE_FOR_PRODUCT_START, getPriceForProduct } from '@app/api/action/Price';
import { RootReducer, Store } from '@app/app.reducers';
import {
	ProductEditorChanges,
	ProductFlowData,
	addProduct,
	getCartItems,
	modifyProduct,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { UploadPrintfileInput } from '@app/file-api/file-api.service';
import {
	ProductPriceRequest,
	ProductType,
	SelectedOptions,
	getPrice,
	getProductById,
	hasFetchedPrice,
} from '@app/product/reducers/product.reducer';
import { getId as getStorefrontId } from '@app/shared/reducers/storefront.reducer';
import { addUnsavedWork, removeUnsavedWork } from '@app/shared/reducers/unsaved-work.reducer';
import { FileUploadStatus, finishUpload, startUpload } from '@app/shared/reducers/upload.reducer';
import { DataLayer } from '@app/shared/services/data-layer.service';
import { NewRelic } from '@app/shared/services/newrelic.service';
import { SeoService } from '@app/shared/services/seo.service';
import { isApiSuccess } from '@app/shared/utils/api-utils';
import { I18nService } from '@app/shared/utils/i18n.service';
import { setName } from '@app/shared/utils/name-helper';
import {
	filterTruthy,
	firstTruthy,
	getDebug,
	isArrayEqual,
	isPlainObjectEqual,
} from '@app/shared/utils/util';
import { getIsSuperAdmin, getUserPermissions } from '@app/user/reducers/user.reducer';
import { ApiError, isApiError } from '@granodigital/grano-remote-data';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom } from 'rxjs';
import {
	distinctUntilChanged,
	distinctUntilKeyChanged,
	filter,
	first,
	map,
	pluck,
	switchMap,
	tap,
} from 'rxjs/operators';

/**
 * Captures an editor state string from from a printfile key
 * @example editor-77805874-217e-4586-9663-52bef6e362fc.pdf
 */
const PRINTFILE_STATE_PATTERN = /^editor-([\da-z]{8}(?:-[\da-z]{4}){3}-[\da-z]{12})/;

/**
 * Product editor service.
 * Note that this is currently used a lot with the product flow to the point of being a common "manager service" for it.
 * Currently, this service is responsible for at least (but not limited to):
 * - Managing the product editor state, which is probably the core functionality that does belong here.
 * - Managing the product flow state.
 * - Connecting file / product data to the cart items.
 * - Storing product and printfile info.
 * TODO: Refactor into smaller services. Only the core functionality should be kept here.
 */
@Injectable({ providedIn: 'root' })
export class ProductEditorService {
	private readonly debug = getDebug('ProductEditorService');
	itemId: string; // ID generated before adding to cart or cart item ID
	workTitle?: string;
	initialOptions: api.OrderProductSelectedOptionsDto = {};
	readonly selectedOptions$ = new BehaviorSubject<SelectedOptions | undefined>({});
	readonly selectedTemplates$ = new BehaviorSubject<string[]>([]);
	readonly productEditorChanges$ = new BehaviorSubject<ProductEditorChanges | undefined>(undefined);
	productFlowData: ProductFlowData;
	isSelectionValid: boolean;
	hasNewPrintfile = false;
	cartItem: api.CartItemWithProductDto;
	readonly product$ = new BehaviorSubject<api.ProductDto | undefined>(undefined);
	readonly price$: Observable<ProductPriceRequest> = this.product$.pipe(
		filterTruthy,
		distinctUntilKeyChanged<api.ProductDto>('id'),
		switchMap((product) => this.store.select(getPrice(product.id))),
	);
	private printfile?: api.PrintfileDto;
	readonly uploadOptions$: Observable<UploadPrintfileInput> = combineLatest([
		this.product$,
		this.selectedOptions$,
		this.price$,
	]).pipe(
		map(
			/* istanbul ignore next -- TODO add test for prototype. */ ([
				product,
				selectedOptions,
				price,
			]) => ({
				storeId: product.store_id,
				productId: product?.id,
				itemId: this.itemId,
				preflightOptions: this.preflight.getPreflightParameters(product, selectedOptions, price.price),
			}),
		),
	);

	constructor(
		private readonly i18n: I18nService,
		private readonly dataLayer: DataLayer,
		private readonly seo: SeoService,
		private readonly store: Store<RootReducer.State>,
		private readonly newRelic: NewRelic,
		private readonly preflight: ProductPreflightService,
	) {
		this.product$.pipe(filterTruthy).subscribe((product) => this.onProductUpdate(product));
		this.connectPrice();
	}

	readonly showPrices$ = this.store
		.select(getUserPermissions)
		.pipe(map((permissions) => permissions?.show_prices ?? true));
	readonly allowDownload$ = this.store
		.select(getUserPermissions)
		.pipe(map((permissions) => permissions?.allow_download ?? false));

	/**
	 * Get product by ID and connect it to product editor
	 * @param productId Product ID
	 */
	connectProductById(productId: number): void {
		this.store
			.select(getProductById(productId))
			.pipe(firstTruthy)
			.subscribe((product) => this.connectProduct(product));
	}

	/**
	 * Connect product editor to a new product
	 * @param product Product object
	 */
	connectProduct(product: api.ProductDto): void {
		this.product$.next(product);
	}

	/**
	 * Connect product editor to an existing cart item.
	 */
	connectCartItem(item: api.CartItemWithProductDto): void {
		// Only react when item changes
		const cartItemId = this.cartItem?.id;
		if (typeof cartItemId === 'string' && !!cartItemId && cartItemId === item.id) return;
		this.cartItem = item;
		this.printfile = item.options.printfile;
		this.connectProduct(item.product);
		this.setProductOptions(item.options, true);
		this.itemId = this.cartItem.id;
		this.hasNewPrintfile = false;
	}

	getPriceValidity$(): Observable<boolean> {
		return combineLatest([this.showPrices$, this.price$]).pipe(
			map(([showPrices, priceState]) => !showPrices || !!priceState?.price?.price_with_tax),
		);
	}

	setProductOptions(
		{ selectedOptions, selectedTemplates, workTitle, productEditorChanges }: api.CartItemOptionsDto,
		isAlwaysValid = false,
	): void {
		if (isAlwaysValid === true) this.isSelectionValid = true;
		this.initialOptions = structuredClone(selectedOptions);
		this.productEditorChanges$.next(productEditorChanges);
		this.selectedOptions$.next(selectedOptions);
		this.selectedTemplates$.next(selectedTemplates || []);
		this.workTitle = workTitle;
	}

	setPrintfile(printfile?: api.PrintfileDto, hasNewPrintfile = !!printfile): void {
		this.printfile = printfile;
		this.hasNewPrintfile = hasNewPrintfile;
	}

	getPrintfile(): api.PrintfileDto | undefined {
		return this.printfile;
	}

	getPrintfileState(): string | undefined {
		if (this.printfile?.editor_state_id) return this.printfile.editor_state_id;
		if (this.printfile?.key) {
			const [, state] = PRINTFILE_STATE_PATTERN.exec(this.printfile.key) || [];
			return state;
		}
	}

	clearUnsavedWork(): void {
		this.store.dispatch(removeUnsavedWork('product-editor'));
	}

	setUnsavedWork(): void {
		this.store.dispatch(addUnsavedWork('product-editor'));
	}

	updateSelectedOptions(event: { isValid: boolean; selectedOptions: SelectedOptions }): void {
		this.isSelectionValid = event.isValid;
		this.selectedOptions$.next(event.selectedOptions);
		this.initialOptions = structuredClone(event.selectedOptions);
		this.debug.extend('update-selected-options')(event.isValid, {
			hasNewPrintfile: this.hasNewPrintfile,
			currentOptions: event.selectedOptions,
			previousOptions: this.initialOptions,
		});
	}

	updateSelectedTemplates(templates: string[]): void {
		this.selectedTemplates$.next(templates);
	}

	getProductAndOptions$(): Observable<
		[api.ProductDto, api.OrderProductSelectedOptionsDto, string[], ProductEditorChanges]
	> {
		return combineLatest([
			this.product$,
			this.selectedOptions$,
			this.selectedTemplates$,
			this.productEditorChanges$,
		]).pipe(
			filter(([, selectedOptions]) => !!selectedOptions),
			distinctUntilChanged(
				([product1, opts1, templates1, changes1], [product2, opts2, templates2, changes2]) =>
					product1?.id === product2?.id &&
					isPlainObjectEqual(opts1, opts2) &&
					isArrayEqual(templates1, templates2) &&
					// TODO: Does not work with nested objects e.g. IBarcodeData!
					isPlainObjectEqual(changes1?.userChanges as any, changes2?.userChanges),
			),
		);
	}

	getAllowDownload$(): Observable<boolean> {
		return combineLatest([this.store.select(getIsSuperAdmin), this.product$, this.allowDownload$]).pipe(
			map(
				([isSuperAdmin, product, permission]) =>
					isSuperAdmin || ((product?.metadata?.allow_download ?? false) === true && permission === true),
			),
		);
	}

	getState$(): Observable<
		[
			ProductWithId,
			api.OrderProductSelectedOptionsDto,
			string[],
			api.ProductPriceDto,
			number,
			ProductEditorChanges,
			ProductFlowData,
		]
	> {
		return combineLatest([
			this.getProductAndOptions$(),
			this.price$.pipe(filter(hasFetchedPrice), pluck('price')),
			this.store.select(getStorefrontId),
		]).pipe(
			map(([[product, selectedOptions, selectedTemplates, productEditorChanges], price, storeId]) => [
				product as ProductWithId,
				selectedOptions,
				selectedTemplates,
				price,
				storeId,
				productEditorChanges,
				this.productFlowData,
			]),
		);
	}

	getSaveableCartItem$(): Observable<api.CartItemWithProductDto> {
		return this.getState$().pipe(
			first(),
			map(
				([
					product,
					selectedOptions,
					selectedTemplates,
					price,
					storeId,
					productEditorChanges,
					productFlowData,
				]) => {
					// Printfile checks in other components expect a falsy printfile
					const printfile = this.preflight.isValidPrintfile(this.printfile)
						? {
								...this.printfile,
								product_id: product.id,
							}
						: undefined;

					const shouldQueuePreflight = this.preflight.shouldQueuePreflight(
						printfile,
						this.hasNewPrintfile,
						selectedOptions,
						this.initialOptions,
					);
					if (shouldQueuePreflight) {
						this.preflight.queuePreflight(this.itemId, product, printfile, selectedOptions, price);
						this.hasNewPrintfile = false;
					}

					const saveableCartItem: api.CartItemWithProductDto = {
						id: this.itemId,
						product,
						options: {
							workTitle: this.workTitle,
							printfile,
							selectedOptions,
							selectedTemplates,
							productEditorChanges,
						},
						price,
						productFlowData,
					};

					if (printfile?.status === 'unprocessed') saveableCartItem.isProcessing = true;
					return saveableCartItem;
				},
			),
		);
	}

	async saveChangesToCart(forceAdd = false): Promise<void> {
		const [
			product,
			selectedOptions,
			selectedTemplates,
			price,
			storeId,
			productEditorChanges,
			productFlowData,
		] = await firstValueFrom(this.getState$());

		// Printfile checks in other components expect a falsy printfile
		const printfile = this.preflight.isValidPrintfile(this.printfile)
			? {
					...this.printfile,
					product_id: product.id,
				}
			: null;

		const params = [
			product,
			printfile,
			selectedOptions,
			selectedTemplates,
			price,
			productEditorChanges,
			productFlowData,
			this.hasNewPrintfile,
		] as const;

		if (
			this.preflight.shouldQueuePreflight(
				printfile,
				this.hasNewPrintfile,
				selectedOptions,
				this.initialOptions,
			)
		) {
			this.preflight.queuePreflight(this.itemId, product, printfile, selectedOptions, price);
			this.hasNewPrintfile = false;
		}

		if (this.cartItem && !forceAdd) {
			this.modifyProduct(this.cartItem.id, ...params);
		} else {
			this.addProduct(...params);
		}

		this.store.dispatch(resetStepData(this.itemId));
	}

	isStateValid$(): Observable<boolean> {
		return combineLatest([
			this.getProductAndOptions$(),
			this.getPriceValidity$(),
			this.store.select(getCartItems),
		]).pipe(
			map(([[product, selectedOptions], isPriceValid, cartItems]) => {
				if (!product) return false;
				const isStockValid =
					product.type !== ProductType.Stock || getStockStatus(product, selectedOptions, cartItems);
				const isValid = this.isSelectionValid && isPriceValid && isStockValid;
				this.debug.extend('is-state-valid')(isValid, {
					isSelectionValid: this.isSelectionValid,
					isPriceValid,
					isStockValid,
				});
				return isValid;
			}),
		);
	}

	patchProductFlowData(data: Partial<api.CartItemProductFlowDataDto> | undefined): void {
		if (!data) return;
		this.productFlowData = { ...this.productFlowData, ...(data as ProductFlowData) };
	}

	async setDefaultWorkTitle(): Promise<void> {
		if (typeof this.workTitle !== 'string' || this.workTitle.length === 0) {
			this.workTitle = await this.getDefaultWorkTitle();
		}
	}

	async getDefaultWorkTitle(): Promise<string> {
		const product: api.ProductDto = await firstValueFrom(this.product$.pipe(firstTruthy));
		return this.i18n.getText(product.locale.product_name);
	}

	resetProductState(): void {
		this.product$.next(undefined);
	}

	resetCartItemState(): void {
		this.cartItem = undefined;
		this.printfile = undefined;
	}

	resetSelectedOptions(): void {
		this.initialOptions = {};
		this.isSelectionValid = false;
		this.selectedOptions$.next({});
		this.workTitle = undefined;
	}

	resetSelectedTemplates(): void {
		this.selectedTemplates$.next([]);
	}

	resetProductFlowData(): void {
		this.productFlowData = { steps: [] };
	}

	/**
	 * Get initial selectable options. Fixes issues with products that have only "hidden" options
	 * (such as order_quantity with only a single option)
	 * @param product Product to use for setting initial options
	 */
	getInitialOptions(product: api.ProductDto): {
		selectedOptions: Record<string, string>;
		isAlwaysValid: boolean;
	} {
		if (!product) return { selectedOptions: {}, isAlwaysValid: false };
		const selectableOptions = product?.properties?.selectableOptions ?? {};
		const textFields = product?.properties?.textFields ?? {};
		const initial = {};
		let isAlwaysValid = true;
		for (const [key, value] of Object.entries<any>(selectableOptions)) {
			if (!value || !Array.isArray(value.selectableValues)) continue;
			if (value.selectableValues.length === 1) initial[key] = value.selectableValues[0];
			else {
				// There are more than 1 selectable option so options aren't always valid
				isAlwaysValid = false;
				initial[key] = '';
			}
		}
		for (const key of Object.keys(textFields)) {
			// There is at least one text field so options aren't always valid
			isAlwaysValid = false;
			initial[key] = '';
		}
		return { selectedOptions: initial, isAlwaysValid };
	}

	async uploadPrintfile(
		stateId: string,
		userId: string,
		shouldThrow = true,
		extension = 'pdf',
	): Promise<void> {
		const debug = this.debug.extend('upload-editor-file');
		const key = `editor-${stateId}.${extension}`;
		this.store.dispatch(setName('startUpload', startUpload(key)));
		const { id: productId } = await firstValueFrom(this.product$.pipe(firstTruthy));

		const response: api.Response<api.PrintfileDto | ApiError> = await uploadEditorFile({
			stateId,
			userId,
			productId: productId!,
			extension,
		});

		debug('result', { key, response });
		if (isApiSuccess(response) && response.raw?.ok) {
			this.setPrintfile(response.data, false);
			this.store.dispatch(finishUpload(key, FileUploadStatus.Success));
		} else {
			this.store.dispatch(finishUpload(key, FileUploadStatus.Error));
			const errorText =
				(isApiError(response)
					? `${response?.data?.message || 'Unknown error'} (${response?.data?.trace || 'unknown'})`
					: response?.raw?.statusText) || 'Unknown error';
			const error = new Error(`File upload failed: ${errorText}`);
			this.newRelic.noticeError(error);
			if (shouldThrow) throw error;
		}
	}

	private connectPrice() {
		this.getProductAndOptions$()
			.pipe(
				filter(([product]) => !!product?.id && !isFormProduct(product)),
				filter(
					([product, options]) =>
						this.isSelectionValid && getProductQuantity({ options, product }, 0) > 0,
				),
				tap(([product]) => {
					this.store.dispatch({
						type: GET_PRICE_FOR_PRODUCT_START,
						meta: { info: { productId: product.id } },
					});
				}),
			)
			.subscribe(([product, options]) => {
				const productId = product.id!;
				this.store.dispatch(getPriceForProduct(productId, { body: options }, { productId }));
			});
	}

	private modifyProduct(
		cartItemId: string,
		product: api.CartItemProductDto,
		printfile: api.PrintfileDto,
		selectedOptions: SelectedOptions,
		selectedTemplates: string[],
		price: api.ProductPriceDto,
		productEditorChanges: ProductEditorChanges = undefined,
		productFlowData: ProductFlowData = undefined,
		hasNewPrintfile = false,
	) {
		this.store.dispatch(
			modifyProduct(
				cartItemId,
				product,
				{
					workTitle: this.workTitle,
					selectedOptions,
					selectedTemplates,
					printfile,
					productEditorChanges,
				},
				price,
				{
					...productFlowData,
					productOptionsSavedAt: Date.now(),
				},
				hasNewPrintfile,
			),
		);
	}

	private addProduct(
		product: ProductWithId,
		printfile: api.PrintfileDto,
		selectedOptions: SelectedOptions,
		selectedTemplates: string[],
		price: api.ProductPriceDto,
		productEditorChanges: ProductEditorChanges = undefined,
		productFlowData: ProductFlowData = undefined,
		hasNewPrintfile: boolean,
	) {
		this.store.dispatch(
			addProduct(
				this.itemId,
				product,
				{
					workTitle: this.workTitle,
					printfile,
					selectedOptions,
					selectedTemplates,
					productEditorChanges,
				},
				{
					...productFlowData,
					productSource: productFlowData.productSource || ProductSource.NewProduct,
					productOptionsSavedAt: productFlowData.productOptionsSavedAt || Date.now(),
				},
				hasNewPrintfile,
			),
		);
		this.dataLayer.addProductToCart({
			id: this.itemId,
			product,
			price,
			options: {},
			productFlowData,
		});
	}

	private readonly onProductUpdate = (product: api.ProductDto) => {
		this.itemId = crypto.randomUUID();
		this.seo.deduceProperties(product);
		const { selectedOptions, isAlwaysValid } = this.getInitialOptions(product);
		this.initialOptions = structuredClone(selectedOptions);
		this.updateSelectedOptions({
			isValid: isAlwaysValid,
			selectedOptions: this.initialOptions,
		});
	};
}
